import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { BookOpen, ArrowRight, Bell, CalendarClock, Award, ShieldCheck, Sparkles, Zap } from "lucide-react";
import UpcomingSessionsWidget from "@/components/UpcomingSessionsWidget";
import { useSubscription } from "@/hooks/useSubscription";

export default function AlumnoDashboard() {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const { hasAllAccess, subscription } = useSubscription();

  useEffect(() => {
    Promise.all([
      api.get("/alumno/enrollments"),
      api.get("/alumno/notifications"),
      api.get("/alumno/live-sessions/upcoming"),
    ]).then(([e, n, u]) => {
      setEnrollments(e.data);
      setNotifs(n.data);
      setUpcoming(u.data.filter((s) => s.is_future).slice(0, 3));
    }).finally(() => setLoading(false));
  }, []);

  const activeCount = enrollments.filter((e) => e.status === "active").length;
  const completedCount = enrollments.filter((e) => e.status === "completed").length;
  const nextEnr = enrollments.find((e) => e.status === "active");

  return (
    <AlumnoLayout user={user}>
      <div>
        <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Campus</p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">Bienvenido, {user?.full_name?.split(" ")[0]}</h1>
        <p className="mt-2 text-gray-400">Continúa donde lo dejaste.</p>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <StatCard label="Programas activos" value={activeCount} icon={BookOpen} />
          <StatCard label="Completados" value={completedCount} icon={Award} />
          <StatCard label="Notificaciones" value={notifs.filter((n) => !n.read_at).length} icon={Bell} />
        </div>

        {hasAllAccess && subscription ? (
          <Link
            to="/app/alumno/suscripcion"
            className="mt-6 block p-4 rounded-xl border border-primary/40 bg-primary/10 hover:bg-primary/15 transition-colors"
            data-testid="alumno-all-access-active-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-[#93C5FD]" />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-bold">All-Access activo</p>
                <p className="text-sm text-white font-semibold">{subscription.plan_name}</p>
                <p className="text-xs text-gray-400">
                  {subscription.cancel_at_period_end ? "Termina" : "Se renueva"} el{" "}
                  {new Date(subscription.current_period_end).toLocaleDateString("es-MX", { day: "numeric", month: "long" })}
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#93C5FD]" />
            </div>
          </Link>
        ) : (
          <Link
            to="/planes"
            data-testid="alumno-all-access-promo"
            className="mt-6 block p-4 rounded-xl border border-dashed border-gray-700 bg-[#111827] hover:border-[#F26A21] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F26A21]/20 border border-[#F26A21]/40 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-[#F26A21]" />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Pago mock
                </p>
                <p className="text-sm text-white font-semibold">
                  Desbloquea 195+ programas con All-Access desde $2,500/mes
                </p>
                <p className="text-xs text-gray-400">
                  Cancela cuando quieras · sin permanencia
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#F26A21]" />
            </div>
          </Link>
        )}

        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <UpcomingSessionsWidget ownerType="alumno" limit={3} />
        </div>

        {nextEnr && (
          <section className="mt-10 p-6 rounded-2xl border border-gray-800 bg-gradient-to-br from-[#111827] via-[#0F172A] to-[#0B0F14] relative overflow-hidden" data-testid="continue-learning-card">
            <div className="absolute inset-0 bg-grid opacity-30" />
            <div className="relative flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Continúa aprendiendo</p>
                <h2 className="mt-2 text-2xl font-bold text-white">{nextEnr.program_title}</h2>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex-1 max-w-sm h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#3B82F6]" style={{ width: `${nextEnr.progress.percent}%` }} />
                  </div>
                  <span className="font-mono text-sm text-white">{nextEnr.progress.percent}%</span>
                </div>
              </div>
              <Link to={`/app/alumno/programas/${nextEnr.program_id}`} data-testid="continue-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold text-sm">
                Continuar <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>
        )}

        <section className="mt-10">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5" /> Mis programas</h2>
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (<div key={i} className="h-40 bg-[#111827] border border-gray-800 rounded-xl animate-pulse" />))}
            </div>
          ) : enrollments.length === 0 ? (
            <div className="p-8 border border-dashed border-gray-800 rounded-xl text-center text-gray-400">
              Aún no tienes programas asignados.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="enrollments-grid">
              {enrollments.map((e) => <EnrollmentCard key={e.id} e={e} />)}
            </div>
          )}
        </section>

        {upcoming.length > 0 && (
          <section className="mt-10">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><CalendarClock className="w-5 h-5" /> Próximas sesiones en vivo</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {upcoming.map((s) => (
                <div key={s.lesson_id} className="p-4 bg-[#111827] border border-gray-800 rounded-xl">
                  <p className="text-xs uppercase tracking-widest text-[#93C5FD]">{s.program_title}</p>
                  <p className="mt-1 font-semibold text-white">{s.title}</p>
                  <p className="mt-1 text-sm text-gray-400 font-mono">{new Date(s.starts_at).toLocaleString("es-MX")}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {notifs.length > 0 && (
          <section className="mt-10">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Bell className="w-5 h-5" /> Últimas notificaciones</h2>
            <div className="space-y-2">
              {notifs.slice(0, 5).map((n) => (
                <div key={n.id} className="p-3 border border-gray-800 rounded-md bg-[#111827] flex items-start gap-3">
                  <div className={`w-1.5 h-1.5 rounded-full mt-2 ${n.read_at ? "bg-gray-600" : "bg-[#3B82F6]"}`} />
                  <div className="flex-1">
                    <p className="text-sm text-white font-medium">{n.title}</p>
                    <p className="text-xs text-gray-400">{n.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </AlumnoLayout>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="p-5 bg-[#111827] border border-gray-800 rounded-xl">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-widest text-gray-500">{label}</p>
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <p className="mt-2 font-mono text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

function EnrollmentCard({ e }) {
  return (
    <Link to={`/app/alumno/programas/${e.program_id}`} data-testid={`enrollment-card-${e.program_id}`}
      className="p-5 bg-[#111827] border border-gray-800 rounded-xl hover:border-[#3B82F6]/60 transition-all group">
      <div className="flex items-start justify-between">
        <span className="text-[10px] uppercase tracking-widest text-gray-400">{e.program_format}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold ${
          e.status === "completed" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" :
          "bg-brand-orange-500/10 text-brand-orange-300 border border-primary/30"
        }`}>{e.status}</span>
      </div>
      <h3 className="mt-2 text-base font-semibold text-white group-hover:text-[#93C5FD]">{e.program_title}</h3>
      <div className="mt-4 flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#3B82F6]" style={{ width: `${e.progress.percent}%` }} />
        </div>
        <span className="font-mono text-xs text-white">{e.progress.percent}%</span>
      </div>
      <p className="mt-2 text-xs text-gray-500">{e.progress.completed}/{e.progress.total} lecciones</p>
    </Link>
  );
}
